import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './pages/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import CallLog from './pages/CallLog';
import Projects from './pages/Projects';
import Operations from './pages/Operations';
import Estimating from './pages/Estimating';
import PriceBook from './pages/PriceBook';
import Requests from './pages/Requests';
import Admin from './pages/Admin';
import Accounts from './pages/Accounts';
import AccountDetail from './pages/AccountDetail';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Calendar from './pages/Calendar';
import SalesLibrary from './pages/SalesLibrary';
import RmQuotes from './pages/RmQuotes';
import Manuals from './pages/Manuals';
import ForcePasswordChange from './pages/ForcePasswordChange';
import { styles } from './components/UI';

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Bounce to forced-change screen if the user hasn't picked their own
  // password yet (every newly-created account starts with this flag true).
  // SKIP this when admin/leadership is impersonating via "View as" --
  // otherwise they get trapped in the target user's password reset flow
  // with no way out (the screen has no exit, and the user record they're
  // viewing still has must_change_password=true).
  if (user.must_change_password && !user.is_preview) return <ForcePasswordChange />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const Home = () => {
  const { user } = useAuth();
  if (user.role === 'ops') return <Navigate to="/operations" replace />;
  return <Navigate to="/dashboard" replace />;
};

export default function App() {
  return (
    <div style={styles.page}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/reset" element={<ResetPassword />} />
        <Route element={<Protected><Layout /></Protected>}>
          <Route path="/" element={<Home />} />
          <Route path="/dashboard" element={<Protected roles={['admin','leadership','supervisor','rep']}><Dashboard /></Protected>} />
          <Route path="/call-log" element={<Protected roles={['admin','leadership','supervisor','rep']}><CallLog /></Protected>} />
          <Route path="/accounts" element={<Protected roles={['admin','leadership','supervisor','rep']}><Accounts /></Protected>} />
          <Route path="/accounts/:name" element={<Protected roles={['admin','leadership','supervisor','rep']}><AccountDetail /></Protected>} />
          {/* ops included read-only (2026-06-11) so dispatch can reach the
              Reqs page and download badging documents. Server enforces
              read-only — writes require admin/leadership/supervisor. */}
          <Route path="/customers" element={<Protected roles={['admin','leadership','supervisor','rep','ops']}><Customers /></Protected>} />
          <Route path="/customers/:id" element={<Protected roles={['admin','leadership','supervisor','rep','ops']}><CustomerDetail /></Protected>} />
          <Route path="/projects" element={<Protected roles={['admin','leadership','supervisor','rep']}><Projects /></Protected>} />
          <Route path="/operations" element={<Protected roles={['admin','leadership','supervisor','ops']}><Operations /></Protected>} />
          <Route path="/estimating" element={<Protected roles={['admin','leadership']}><Estimating /></Protected>} />
          <Route path="/price-book" element={<Protected roles={['admin','leadership']}><PriceBook /></Protected>} />
          <Route path="/requests"   element={<Protected roles={['admin','leadership','supervisor','rep']}><Requests /></Protected>} />
          <Route path="/calendar" element={<Protected roles={['admin','leadership','supervisor','rep']}><Calendar /></Protected>} />
          <Route path="/rm-quotes" element={<Protected roles={['admin','leadership','supervisor','rep']}><RmQuotes /></Protected>} />
          <Route path="/sales-library" element={<Protected roles={['admin','leadership','supervisor','rep','ops']}><SalesLibrary /></Protected>} />
          <Route path="/manuals" element={<Protected roles={['admin','leadership','supervisor','rep','ops']}><Manuals /></Protected>} />
          <Route path="/admin" element={<Protected roles={['admin','leadership']}><Admin /></Protected>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
